<template>
  <b-card
    v-if="!!option"
    id="page-inventory-movement-form"
    no-body
    class="mb-0"
  >
    <b-card-header>
      <b-card-title>
        <h2>
          {{ $t(getTitle()) }} {{ $t("pages.inventories.title").toLowerCase() }}
        </h2>
      </b-card-title>
      <b-button variant="primary" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" />
        {{ $t("back") }}
      </b-button>
    </b-card-header>

    <b-card-body>
      <!-- <b-alert v-if="!movement.guidCompany" show variant="warning">
        {{ $t(label("selectCompany")) }}
      </b-alert> -->

      <validation-observer ref="formRules">
        <b-form>
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group
                :label="$t(label('movementType'))"
                label-for="movementTypeCode"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('movementType'))"
                  rules="required"
                >
                  <v-select
                    id="movementTypeCode"
                    v-model="movement.movementTypeCode"
                    :options="movementTypes"
                    label="name"
                    :reduce="(option) => option.code"
                    :disabled="!isSaveMode"
                    :placeholder="$t(label('selectMovementType'))"
                    @input="onMovementTypeChange"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col v-if="requiresSupplier" cols="12" md="6">
              <b-form-group :label="$t(label('supplier'))" label-for="supplier">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('supplier'))"
                  rules="required"
                >
                  <v-select
                    id="supplier"
                    v-model="movement.idSupplier"
                    :options="suppliers"
                    label="name"
                    :reduce="(option) => option.idSupplier"
                    :disabled="!isSaveMode"
                    :placeholder="$t(label('selectSupplier'))"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group
                :label="$t(label('documentNumber'))"
                label-for="documentNumber"
              >
                <b-form-input
                  id="documentNumber"
                  v-model="movement.documentNumber"
                  :placeholder="$t(label('documentNumber'))"
                  :disabled="!isSaveMode"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group
                :label="$t(label('description'))"
                label-for="description"
              >
                <b-form-input
                  id="description"
                  v-model="movement.description"
                  :placeholder="$t(label('description'))"
                  :disabled="!isSaveMode"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <hr />

          <div class="d-flex justify-content-between align-items-center mb-1">
            <h4 class="mb-0">{{ $t(label("products")) }}</h4>
            <b-button
              v-if="isSaveMode"
              variant="outline-primary"
              size="sm"
              @click="addDetail"
            >
              <feather-icon icon="PlusIcon" class="mr-25" />
              {{ $t(label("addProduct")) }}
            </b-button>
          </div>

          <b-row
            v-for="(detail, index) in movement.details"
            :key="detail.localId"
            class="border rounded p-1 mb-1"
          >
            <b-col cols="12" md="4">
              <b-form-group :label="$t(label('product'))">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('product'))"
                  rules="required"
                >
                  <v-select
                    v-model="detail.guidProduct"
                    :options="products"
                    label="name"
                    :reduce="(option) => option.guid"
                    :disabled="!isSaveMode"
                    :placeholder="$t(label('selectProduct'))"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="2">
              <b-form-group :label="$t(label('quantity'))">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('quantity'))"
                  rules="required|positive"
                >
                  <b-form-input
                    v-model="detail.quantity"
                    type="number"
                    min="1"
                    :disabled="!isSaveMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="2">
              <b-form-group :label="$t(label('unitCost'))">
                <b-form-input
                  v-model="detail.unitCost"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="!isSaveMode"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="2">
              <b-form-group :label="$t(label('unitPrice'))">
                <b-form-input
                  v-model="detail.unitPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="!isSaveMode"
                />
              </b-form-group>
            </b-col>

            <b-col v-if="!isSaveMode" cols="12" md="4">
              <b-form-group :label="$t(label('stockBefore'))">
                <b-form-input :value="detail.stockBefore" disabled />
              </b-form-group>
            </b-col>

            <b-col v-if="!isSaveMode" cols="12" md="4">
              <b-form-group :label="$t(label('stockAfter'))">
                <b-form-input :value="detail.stockAfter" disabled />
              </b-form-group>
            </b-col>

            <b-col cols="12" :md="isSaveMode ? 10 : 12">
              <b-form-group :label="$t(label('notes'))">
                <b-form-textarea
                  id="notes"
                  v-model="detail.notes"
                  :placeholder="$t(label('notes'))"
                  :disabled="!isSaveMode"
                  rows="4"
                />
              </b-form-group>
            </b-col>

            <b-col
              v-if="isSaveMode"
              cols="12"
              md="2"
              class="d-flex align-items-center justify-content-end"
            >
              <b-button
                variant="outline-danger"
                size="sm"
                :disabled="movement.details.length === 1"
                @click="removeDetail(index)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </b-col>
          </b-row>

          <b-col
            class="d-flex justify-content-end align-items-center"
            cols="12"
          >
            <b-button
              v-if="isSaveMode"
              variant="primary"
              class="mr-1"
              :disabled="!movement.guidCompany"
              @click.prevent="onSubmit"
            >
              {{ $t("save") }}
            </b-button>
            <b-button
              v-if="option === 1"
              variant="outline-secondary"
              @click="onCancel"
            >
              {{ $t("cancel") }}
            </b-button>
          </b-col>
        </b-form>
      </validation-observer>
    </b-card-body>
  </b-card>
</template>

<script>
import _ from "lodash";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BFormTextarea,
} from "bootstrap-vue";
import vSelect from "vue-select";
import router from "@/router";
import {
  allMovementTypes,
  createInventoryMovements,
  getInventoryMovement,
} from "@core/api/request/inventories";
import { allProducts } from "@core/api/request/products";
import { allSuppliers } from "@core/api/request/suppliers";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";
import { positive, required } from "@validations";

const movementTemplate = {
  guidCompany: null,
  idSupplier: null,
  movementTypeCode: null,
  documentNumber: "",
  description: "",
  details: [],
};

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BFormTextarea,
  },
  data: () => ({
    option: "",
    isSaveMode: true,
    movement: {
      ...movementTemplate,
      details: [],
    },
    movementTypes: [],
    suppliers: [],
    products: [],
    positive,
    required,
  }),
  computed: {
    requiresSupplier() {
      return this.movement.movementTypeCode === "PURCHASE_IN";
    },
  },
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      if (this.option === 1) {
        this.movement.guidCompany = guid;
        this.loadCatalogs();
      }
    },
  },
  created() {
    localize("es");
    this.initialize();
  },
  methods: {
    label(field) {
      return `pages.inventories.form.label.${field}`;
    },
    getTitle() {
      if (this.option === 1) return "register";
      return "show";
    },
    async initialize() {
      this.option = this.$route.params.option;
      if (this.option === undefined) {
        router.push({ name: "inventories" }).catch(() => {});
        return;
      }

      this.isSaveMode = this.option === 1;
      this.onCancel();
      await this.loadCatalogs();

      if (this.option === 2) {
        if (this.$route.params.guid === undefined) {
          router.push({ name: "inventories" }).catch(() => {});
          return;
        }
        this.showMovement(this.$route.params.guid);
      }
    },
    normalizeArray(resp) {
      const data = _.get(resp, "data");
      return (
        data?.items ||
        data?.listOfItems?.items ||
        data?.data?.items ||
        (Array.isArray(data) ? data : [])
      );
    },
    async loadCatalogs() {
      if (!this.movement.guidCompany) return;
      try {
        showLoading();
        const [movementTypesResp, suppliersResp, productsResp] =
          await Promise.all([
            allMovementTypes(),
            allSuppliers(
              getParams([
                "page,1",
                "rows,999",
                `guidCompany,${this.movement.guidCompany}`,
                "active,true",
              ]),
            ),
            allProducts(
              getParams([
                "page,1",
                "rows,999",
                `guidCompany,${this.movement.guidCompany}`,
              ]),
            ),
          ]);

        this.movementTypes = this.normalizeArray(movementTypesResp).map(
          (item) => ({
            ...item,
            code: item.code || item.movementTypeCode,
            name: item.name || item.description || item.code,
          }),
        );
        this.suppliers = this.normalizeArray(suppliersResp);
        this.products = this.normalizeArray(productsResp);
      } catch (error) {
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      } finally {
        stopLoading();
      }
    },
    showMovement(guid) {
      showLoading();
      getInventoryMovement(guid)
        .then((resp) => {
          const data = _.get(resp, "data", {});
          this.movement = {
            guidCompany: data.guidCompany || this.movement.guidCompany,
            idSupplier: data.idSupplier || null,
            movementTypeCode: data.movementTypeCode || data.typeCode || null,
            documentNumber: data.documentNumber || "",
            description: data.description || "",
            details: this.normalizeDetails(data.details || data.products || []),
          };
        })
        .catch((error) => {
          this.alertToast(
            "danger",
            "Error",
            error.message,
            "AlertTriangleIcon",
          );
        })
        .finally(() => {
          stopLoading();
        });
    },
    normalizeDetails(details) {
      return details.map((item, index) => ({
        localId: `${Date.now()}-${index}`,
        guidProduct: item.guidProduct || item.productGuid,
        productName: item.productName || item.nameProduct,
        quantity: item.quantity,
        unitCost: item.unitCost,
        unitPrice: item.unitPrice,
        stockBefore: item.stockBefore,
        stockAfter: item.stockAfter,
        notes: item.notes,
      }));
    },
    addDetail() {
      this.movement.details.push({
        localId: `${Date.now()}-${Math.random()}`,
        guidProduct: null,
        quantity: "",
        unitCost: "",
        unitPrice: "",
        notes: "",
      });
    },
    removeDetail(index) {
      this.movement.details.splice(index, 1);
    },
    onMovementTypeChange() {
      if (!this.requiresSupplier) {
        this.movement.idSupplier = null;
      }
    },
    onSubmit() {
      this.$refs.formRules.validate().then((success) => {
        if (!success) return;
        showLoading();
        createInventoryMovements(this.buildPayload())
          .then(() => {
            this.alertToast(
              "success",
              this.$t("success"),
              this.$t("transactionSuccess"),
              "CheckIcon",
            );
            this.goBack();
          })
          .catch((error) => {
            this.alertToast(
              "danger",
              "Error",
              error.message,
              "AlertTriangleIcon",
            );
          })
          .finally(() => {
            stopLoading();
          });
      });
    },
    buildPayload() {
      return {
        guidCompany: this.movement.guidCompany,
        idSupplier: this.requiresSupplier ? this.movement.idSupplier : null,
        movementTypeCode: this.movement.movementTypeCode,
        documentNumber: this.movement.documentNumber || null,
        description: this.movement.description || null,
        details: this.movement.details.map((detail) => ({
          guidProduct: detail.guidProduct,
          quantity: this.toNumber(detail.quantity),
          unitCost: this.toNullableNumber(detail.unitCost),
          unitPrice: this.toNullableNumber(detail.unitPrice),
          notes: detail.notes || null,
        })),
      };
    },
    toNumber(value) {
      return Number(value || 0);
    },
    toNullableNumber(value) {
      if (value === "" || value === null || value === undefined) return null;
      return Number(value);
    },
    goBack() {
      if (this.$refs.formRules) {
        this.$refs.formRules.reset();
      }
      router.back();
    },
    onCancel() {
      this.movement = {
        ...movementTemplate,
        guidCompany: this.$store.state.appConfig.activeCompanyGuid,
        details: [],
      };
      this.addDetail();
    },
    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
